import { CustomDate, deepMap } from '@nemuru-eng/js-api-client';

import { CartRepository } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartRepository/CartRepository';
import { StoredCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/StoredCart';

export class CartLocalStorageRepository implements CartRepository {
  private readonly storageKey = 'cart_repository';

  private getAll(): Record<string, StoredCart[]> {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (!data) return {};
      return JSON.parse(data);
    } catch (err) {
      if (err instanceof DOMException) {
        console.warn('Local storage read-access restricted');
        console.warn('Failed to getAll');
        return {};
      }
      throw err;
    }
  }

  private castDates(onboardingDetails: StoredCart): StoredCart {
    return deepMap(
      onboardingDetails,
      (key) => key,
      (value) => {
        if (CustomDate.isLocalDateTimeString(value)) {
          return new CustomDate(value);
        }
        return value;
      },
    );
  }

  private isCartOlderThan2Weeks(cart: StoredCart): boolean {
    return new Date(cart.createdAt) < new CustomDate().subtractDays(15);
  }

  public get(agentId: string): StoredCart[] {
    const data = this.getAll();
    const carts = data[agentId];
    if (!carts) return [];

    carts.forEach((cart) => {
      if (this.isCartOlderThan2Weeks(this.castDates(cart))) {
        this.remove(agentId, cart.id);
      }
    });
    return carts.map((cart) => {
      return this.castDates(cart);
    });
  }

  public set(agentId: string, checkoutSessionId: string, cart: StoredCart): void {
    const data = this.getAll();
    const filteredCarts = (data[agentId] ?? []).filter((storedCart) => storedCart.id !== checkoutSessionId);

    const newCart = { ...cart, id: checkoutSessionId };

    localStorage.setItem(
      this.storageKey,
      JSON.stringify({
        ...(data ?? {}),
        [agentId]: [...filteredCarts, newCart],
      }),
    );
  }

  public remove(agentId: string, id: string): void {
    const data = this.getAll();
    data[agentId] = data[agentId].filter((cart) => cart.id !== id);
    localStorage.setItem(this.storageKey, JSON.stringify(data));
    return;
  }
}
